import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, CalendarDays, Ticket, MapPin, Sparkles, List as ListIcon,
  CalendarRange, ChevronLeft, ChevronRight, ArrowRight,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import TagsMenuBar from "@/components/TagsMenuBar";
import ServiceBooking from "@/components/ServiceBooking";
import { fetchEvents } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function ymd(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function StatCard({ icon: Icon, value, label, tint, testId }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm px-4 py-4 flex items-center gap-3" data-testid={testId}>
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-zinc-900 leading-none">{value}</div>
        <div className="text-xs text-zinc-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

function EventCard({ ev, dealerSlug }) {
  const d = ev.date_time ? new Date(ev.date_time) : null;
  return (
    <Link
      to={`/events/${ev.slug}${dealerSlug ? `?d=${dealerSlug}` : ""}`}
      className="group flex gap-4 rounded-2xl border border-zinc-200 bg-white shadow-sm p-4 hover:border-violet-300 hover:shadow-md transition-all"
      data-testid={`event-card-${ev.slug}`}
    >
      <div className="shrink-0 w-16">
        {ev.image ? (
          <div className="w-16 h-16 rounded-xl overflow-hidden bg-zinc-50 border border-zinc-100">
            <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.parentElement.innerHTML = ''; }} />
          </div>
        ) : (
          <div className="w-16 rounded-xl bg-gradient-to-br from-violet-600 to-violet-800 text-white flex flex-col items-center justify-center py-2">
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {d ? d.toLocaleString("en-US", { month: "short" }) : "—"}
            </span>
            <span className="text-xl font-bold leading-none">{d ? d.getDate() : "?"}</span>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-zinc-900 leading-snug line-clamp-2">{ev.title}</h3>
          {ev.featured && (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
              Featured
            </span>
          )}
        </div>
        <div className="text-xs text-zinc-500 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={12} /> {ev.date_label || (d ? d.toLocaleString() : "")}
          </span>
          {(ev.city || ev.company) && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} /> {[ev.company, ev.city, ev.state].filter(Boolean).join(", ")}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          {ev.spots_left != null ? (
            <span className={`text-xs font-semibold ${ev.spots_left > 0 ? "text-emerald-700" : "text-rose-600"}`}>
              {ev.spots_left > 0 ? `${ev.spots_left} spots left` : "Fully booked"}
            </span>
          ) : <span />}
          <span className="text-xs font-semibold text-violet-700 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
            View <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function MiniCalendar({ month, setMonth, eventDays, selected, onSelect }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-3" data-testid="events-mini-calendar">
      <div className="flex items-center justify-between mb-2 px-1">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-7 w-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Previous month" data-testid="mini-cal-prev">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-bold text-zinc-800">
          {month.toLocaleString("en-US", { month: "long", year: "numeric" })}
        </span>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-7 w-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Next month" data-testid="mini-cal-next">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DOW.map((d) => (
          <div key={d} className="text-[10px] font-semibold text-zinc-400 py-1">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const key = localYmd(new Date(month.getFullYear(), month.getMonth(), d));
          const has = eventDays.has(key);
          const isSel = selected === key;
          return (
            <button
              key={i}
              onClick={() => onSelect(isSel ? "" : key)}
              className={`relative h-8 rounded-lg text-xs font-medium transition-colors ${
                isSel ? "bg-violet-600 text-white" : has ? "text-violet-700 hover:bg-violet-50" : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid={`mini-cal-day-${key}`}
            >
              {d}
              {has && !isSel && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-violet-500" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthGrid({ month, setMonth, byDay, dealerSlug }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="events-month-grid">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <span className="font-bold text-zinc-800">{month.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-8 w-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500" aria-label="Next month">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {DOW.map((d) => <div key={d} className="text-[11px] font-semibold text-zinc-400 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[72px]" />;
          const key = localYmd(new Date(month.getFullYear(), month.getMonth(), d));
          const evs = byDay.get(key) || [];
          return (
            <div key={i} className="min-h-[72px] rounded-lg border border-zinc-100 p-1 text-left">
              <div className="text-[11px] font-semibold text-zinc-500">{d}</div>
              <div className="space-y-0.5 mt-0.5">
                {evs.slice(0, 2).map((ev) => (
                  <Link key={ev.slug} to={`/events/${ev.slug}${dealerSlug ? `?d=${dealerSlug}` : ""}`} className="block truncate text-[10px] font-medium text-white bg-violet-600 rounded px-1 py-0.5 hover:bg-violet-700" title={ev.title}>
                    {ev.title}
                  </Link>
                ))}
                {evs.length > 2 && <div className="text-[10px] text-zinc-400">+{evs.length - 2} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { user } = useAuth();
  const { slug: dealerSlug } = useParams();
  const callerRecId = user?.user_record_id || user?.record_id || "";
  const [data, setData] = useState({ events: [], stats: {}, cities: [], role: "public" });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState("");
  const [month, setMonth] = useState(() => new Date());

  useEffect(() => {
    document.title = "Events Calendar · OreVital";
    let alive = true;
    setLoading(true);
    fetchEvents(callerRecId, dealerSlug)
      .then((d) => alive && setData(d))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [callerRecId, dealerSlug]);

  const events = data.events || [];
  const eventDays = useMemo(() => new Set(events.map((e) => ymd(e.date_time)).filter(Boolean)), [events]);
  const byDay = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const k = ymd(e.date_time);
      if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [events]);

  const listEvents = selected ? events.filter((e) => ymd(e.date_time) === selected) : events;
  const stats = data.stats || {};

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f4]" data-testid="events-page">
      <PublicHeader active="events" nextPath="/events" />
      <TagsMenuBar />

      {/* Title bar */}
      <div className="bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <CalendarDays size={24} /> {data.dealer_name ? `${data.dealer_name} — Wellness Events` : "Events Calendar"}
          </h1>
          <div className="flex items-center rounded-xl bg-white/10 p-1" data-testid="events-view-toggle">
            <button
              onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors ${view === "list" ? "bg-white text-zinc-900" : "text-white/80"}`}
              data-testid="events-view-list"
            >
              <ListIcon size={15} /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors ${view === "calendar" ? "bg-white text-zinc-900" : "text-white/80"}`}
              data-testid="events-view-calendar"
            >
              <CalendarRange size={15} /> Calendar
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={CalendarDays} value={stats.upcoming ?? 0} label="Upcoming Events" tint="bg-blue-50 text-blue-600" testId="stat-upcoming" />
          <StatCard icon={Ticket} value={stats.available_spots ?? 0} label="Available Spots" tint="bg-violet-50 text-violet-600" testId="stat-spots" />
          <StatCard icon={MapPin} value={stats.locations ?? 0} label="Locations" tint="bg-amber-50 text-amber-600" testId="stat-locations" />
          <StatCard icon={Sparkles} value={stats.wellness ?? 0} label="Wellness Events" tint="bg-rose-50 text-rose-600" testId="stat-wellness" />
        </div>

        {loading ? (
          <div className="py-24 text-center text-zinc-500">
            <Loader2 className="animate-spin inline mr-2" size={18} /> Loading events…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
            {/* Main panel */}
            <div className="min-w-0">
              {view === "calendar" ? (
                <MonthGrid month={month} setMonth={setMonth} byDay={byDay} dealerSlug={dealerSlug} />
              ) : events.length === 0 ? (
                <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm py-20 text-center" data-testid="events-empty">
                  <CalendarDays size={40} className="mx-auto text-zinc-300" />
                  <p className="mt-4 text-lg font-bold text-zinc-800">No upcoming events</p>
                  <p className="text-sm text-zinc-500 mt-1">Check back soon for new events and workshops!</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="events-list">
                  {selected && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-zinc-700">
                        {new Date(selected).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      </span>
                      <button onClick={() => setSelected("")} className="text-violet-700 font-semibold hover:underline" data-testid="events-clear-filter">
                        Show all
                      </button>
                    </div>
                  )}
                  {listEvents.length === 0 ? (
                    <div className="text-sm text-zinc-500 py-6 text-center">No events on this day.</div>
                  ) : (
                    listEvents.map((ev) => <EventCard key={ev.slug} ev={ev} dealerSlug={dealerSlug} />)
                  )}
                </div>
              )}
            </div>

            {/* Right skinny panel — services (dealer) + mini calendar + upcoming list */}
            <aside className="space-y-4 lg:sticky lg:top-24" data-testid="events-sidebar">
              {dealerSlug && (data.dealer_services || []).length > 0 ? (
                <ServiceBooking
                  services={data.dealer_services}
                  blockedDates={data.blocked_dates || []}
                  locationId={data.dealer_location_id || ""}
                  dealerName={data.dealer_name || ""}
                />
              ) : (
                <MiniCalendar month={month} setMonth={setMonth} eventDays={eventDays} selected={selected} onSelect={setSelected} />
              )}
              <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-gradient-to-br from-violet-600 to-violet-800 text-white px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-200">Upcoming</div>
                  <h3 className="text-sm font-bold mt-0.5">Next events</h3>
                </div>
                <div className="p-2 space-y-1.5 max-h-[360px] overflow-y-auto">
                  {events.length === 0 ? (
                    <p className="text-xs text-zinc-400 px-2 py-3 text-center">No upcoming events.</p>
                  ) : (
                    events.slice(0, 8).map((ev) => {
                      const d = ev.date_time ? new Date(ev.date_time) : null;
                      return (
                        <Link key={ev.slug} to={`/events/${ev.slug}${dealerSlug ? `?d=${dealerSlug}` : ""}`} className="flex items-center gap-2.5 rounded-xl p-2 hover:bg-zinc-50" data-testid={`sidebar-event-${ev.slug}`}>
                          <div className="shrink-0 w-10 rounded-lg bg-violet-50 text-violet-700 flex flex-col items-center justify-center py-1">
                            <span className="text-[9px] font-bold uppercase">{d ? d.toLocaleString("en-US", { month: "short" }) : "—"}</span>
                            <span className="text-sm font-bold leading-none">{d ? d.getDate() : "?"}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-zinc-900 truncate">{ev.title}</div>
                            {ev.city && <div className="text-[11px] text-zinc-500 truncate">{ev.city}</div>}
                          </div>
                        </Link>
                      );
                    })
                  )}
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
